import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  Eye,
  Monitor,
  TrendingUp,
  Clock,
  Users,
  Target,
  Activity,
  BarChart3,
  Download,
  Server,
  Laptop,
  ChevronDown,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

export default function CampaignReportPage() {
  const { campaignId } = useParams();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => {
    loadReport();
  }, [campaignId]);

  const loadReport = async () => {
    try {
      const response = await api.get(`/campaigns/${campaignId}/report`);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load campaign report');
    } finally {
      setLoading(false);
    }
  };

  // Server-side PDF generation
  const downloadServerPDF = async () => {
    setDownloading(true);
    setDownloadType('server');
    try {
      const response = await api.get(`/campaigns/${campaignId}/report/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `campaign_report_${campaignId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded (Server)');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
      setDownloadType(null);
    }
  };

  // Client-side PDF generation
  const downloadClientPDF = async () => {
    if (!report) return;
    
    setDownloading(true);
    setDownloadType('client');
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = 20;
      
      // Header
      doc.setFillColor(9, 9, 11);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Campaign Report', 20, 25);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`, 20, 35);
      
      yPos = 55;
      
      // Campaign Info Section
      doc.setTextColor(59, 130, 246);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Campaign Information', 20, yPos);
      yPos += 10;
      
      doc.setTextColor(50, 50, 50);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      
      const campaignInfo = [
        ['Campaign Name', report.campaign_name],
        ['Client', report.client_name],
        ['Status', report.status.toUpperCase()],
        ['Period', `${format(new Date(report.start_date), 'MMM d, yyyy')} - ${format(new Date(report.end_date), 'MMM d, yyyy')}`],
        ['Budget', `$${report.budget.toLocaleString()}`],
        ['Spent', `$${report.spent.toLocaleString()}`],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [],
        body: campaignInfo,
        theme: 'plain',
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [100, 100, 100], cellWidth: 50 },
          1: { textColor: [30, 30, 30] }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // KPIs Section
      doc.setTextColor(59, 130, 246);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Performance Indicators', 20, yPos);
      yPos += 10;
      
      const kpis = [
        ['Total Impressions', report.total_impressions.toLocaleString()],
        ['Total Screens', report.total_screens.toString()],
        ['Reach', report.performance_metrics.reach.toLocaleString()],
        ['CPM', `$${report.performance_metrics.cpm}`],
        ['Engagement Rate', `${report.performance_metrics.engagement_rate}%`],
        ['Avg View Time', `${report.performance_metrics.avg_view_time}s`],
        ['Completion Rate', `${report.performance_metrics.completion_rate}%`],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: kpis,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 4 },
        margin: { left: 20, right: 20 }
      });
      
      yPos = doc.lastAutoTable.finalY + 15;
      
      // Screen Performance
      if (report.screen_breakdown && report.screen_breakdown.length > 0) {
        // Check if we need a new page
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setTextColor(59, 130, 246);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Screen Performance', 20, yPos);
        yPos += 10;
        
        const screenData = report.screen_breakdown.map(screen => [
          screen.screen_name,
          screen.impressions.toLocaleString(),
          `$${screen.revenue.toFixed(2)}`,
          `${screen.uptime}%`
        ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Screen', 'Impressions', 'Revenue', 'Uptime']],
          body: screenData,
          theme: 'striped',
          headStyles: { fillColor: [16, 185, 129], textColor: 255 },
          styles: { fontSize: 9, cellPadding: 3 },
          margin: { left: 20, right: 20 }
        });
        
        yPos = doc.lastAutoTable.finalY + 15;
      }
      
      // Daily Impressions Chart Data as Table
      if (report.daily_impressions && report.daily_impressions.length > 0) {
        if (yPos > pageHeight - 80) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setTextColor(59, 130, 246);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Daily Performance Summary', 20, yPos);
        yPos += 10;
        
        const dailyData = report.daily_impressions.slice(-10).map(day => [
          day.date,
          day.impressions.toLocaleString()
        ]);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Date', 'Impressions']],
          body: dailyData,
          theme: 'striped',
          headStyles: { fillColor: [139, 92, 246], textColor: 255 },
          styles: { fontSize: 9, cellPadding: 3 },
          margin: { left: 20, right: 20 }
        });
      }
      
      // Footer on each page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Lumina OS - Campaign Report | Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
      
      // Save the PDF
      doc.save(`campaign_report_${report.campaign_name.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`);
      toast.success('PDF generated (Client-side)');
      
    } catch (error) {
      console.error('Client PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
      setDownloadType(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
      active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      paused: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      completed: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.draft}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="glass-card rounded-xl p-12 text-center">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
        <h3 className="text-xl font-semibold text-white mb-2">Report not found</h3>
        <p className="text-zinc-400 mb-6">The campaign report could not be loaded.</p>
        <Link to="/campaigns">
          <Button className="bg-blue-600 hover:bg-blue-500">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Campaigns
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaign-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <Link to="/campaigns" className="inline-flex items-center text-zinc-400 hover:text-white mb-2">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Campaigns
          </Link>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            {report.campaign_name}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-zinc-400">{report.client_name}</span>
            {getStatusBadge(report.status)}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="border-zinc-700"
              disabled={downloading}
              data-testid="export-pdf-btn"
            >
              {downloading ? (
                <>
                  <div className="w-4 h-4 border-2 border-zinc-400/30 border-t-zinc-400 rounded-full animate-spin mr-2" />
                  {downloadType === 'server' ? 'Generating (Server)...' : 'Generating (Client)...'}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                  <ChevronDown className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
            <DropdownMenuItem 
              onClick={downloadClientPDF}
              className="cursor-pointer hover:bg-zinc-800"
              data-testid="export-pdf-client"
            >
              <Laptop className="w-4 h-4 mr-2 text-blue-400" />
              <div>
                <div className="text-white">Client-side PDF</div>
                <div className="text-xs text-zinc-500">Fast, generated in browser</div>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={downloadServerPDF}
              className="cursor-pointer hover:bg-zinc-800"
              data-testid="export-pdf-server"
            >
              <Server className="w-4 h-4 mr-2 text-emerald-400" />
              <div>
                <div className="text-white">Server-side PDF</div>
                <div className="text-xs text-zinc-500">Full formatting, ReportLab</div>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-500" />
              </div>
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {report.total_impressions.toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Total Impressions</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                ${report.spent.toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Spent of ${report.budget.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                {report.performance_metrics.reach.toLocaleString()}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Estimated Reach</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <Target className="w-6 h-6 text-amber-500" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                ${report.performance_metrics.cpm}
              </p>
              <p className="text-sm text-zinc-400 mt-1">Cost per 1000 Views</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Impressions Chart */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Daily Impressions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={report.daily_impressions}>
                  <defs>
                    <linearGradient id="impressionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a" 
                    fontSize={12}
                    tickFormatter={(value) => format(new Date(value), 'MMM d')}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="impressions"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#impressionsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Hourly Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={report.hourly_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#71717a" 
                    fontSize={12}
                    tickFormatter={(value) => `${value}:00`}
                  />
                  <YAxis stroke="#71717a" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '8px'
                    }}
                    labelFormatter={(value) => `${value}:00`}
                  />
                  <Bar dataKey="impressions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics & Screen Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Metrics */}
        <Card className="glass-card border-zinc-800">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-500" />
                <span className="text-zinc-300">Engagement Rate</span>
              </div>
              <span className="text-white font-semibold">{report.performance_metrics.engagement_rate}%</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-purple-500" />
                <span className="text-zinc-300">Avg. View Time</span>
              </div>
              <span className="text-white font-semibold">{report.performance_metrics.avg_view_time}s</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-emerald-500" />
                <span className="text-zinc-300">Completion Rate</span>
              </div>
              <span className="text-white font-semibold">{report.performance_metrics.completion_rate}%</span>
            </div>
          </CardContent>
        </Card>

        {/* Screen Breakdown */}
        <Card className="glass-card border-zinc-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Screen Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            {report.screen_breakdown.length === 0 ? (
              <div className="text-center py-8">
                <Monitor className="w-12 h-12 mx-auto text-zinc-700 mb-3" />
                <p className="text-zinc-500">No screens assigned to this campaign</p>
              </div>
            ) : (
              <div className="space-y-3">
                {report.screen_breakdown.map((screen, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{screen.screen_name}</p>
                        <p className="text-sm text-zinc-500 truncate">{screen.location}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold">{screen.impressions.toLocaleString()}</p>
                      <p className="text-sm text-emerald-500">{screen.uptime}% uptime</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign Info */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Campaign Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <p className="text-zinc-500 text-sm">Start Date</p>
              <p className="text-white font-medium mt-1">
                {format(new Date(report.start_date), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <p className="text-zinc-500 text-sm">End Date</p>
              <p className="text-white font-medium mt-1">
                {format(new Date(report.end_date), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Total Screens</p>
              <p className="text-white font-medium mt-1">{report.total_screens}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-sm">Budget Utilization</p>
              <p className="text-white font-medium mt-1">
                {((report.spent / report.budget) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
